import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(name, email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Could not create account");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative soft-gradient p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2.5 w-fit">
          <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center">
            <Activity className="h-4 w-4 text-sky-200" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">
            Shushrut<span className="text-sky-500">.</span>AI
          </span>
        </Link>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num text-slate-500 mb-4">
            Join the research preview
          </div>
          <h2 className="font-heading text-4xl tracking-tight text-slate-900 font-light leading-[1.1] max-w-md">
            Build a dermatology <em className="not-italic text-slate-400">memory</em> that grows with every case.
          </h2>
        </div>
        <div className="text-xs text-slate-500 font-mono-num tracking-wider">
          © Shushrut AI · Research Preview
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-10" data-testid="register-back">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">Create account</div>
          <h1 className="font-heading mt-2 text-4xl tracking-tight text-slate-900 font-light">Let's get you set up.</h1>
          <p className="mt-3 text-slate-600">Your dashboard saves every scan and agent trace.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] font-mono-num text-slate-500">Name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dr. Jane Doe"
                className="mt-2 h-12 rounded-xl border-slate-200 bg-slate-50"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-mono-num text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@clinic.com"
                className="mt-2 h-12 rounded-xl border-slate-200 bg-slate-50"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-mono-num text-slate-500">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="min. 6 characters"
                className="mt-2 h-12 rounded-xl border-slate-200 bg-slate-50"
                data-testid="register-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-slate-900 font-medium hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
